import React, { useState, useEffect } from 'react';
import { 
  previewInvoiceNumber, 
  formatCurrency, 
  calculateVAT,
  calculateGrandTotal 
} from '../utils/invoiceUtils';

const InvoiceForm = ({ onPreview, onSave }) => {
  // Invoice type
  const [invoiceType, setInvoiceType] = useState('Final Invoice');
  
  // Basic info
  const [matterCode, setMatterCode] = useState('L/031/22');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryMethod, setDeliveryMethod] = useState('Courier');
  
  // Verification QR Code
  const [verificationQR, setVerificationQR] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  
  // Firm Stamp Image
  const [stampImage, setStampImage] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  
  // Signature Image
  const [signatureImage, setSignatureImage] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  
  // Client info
  const [clientName, setClientName] = useState('');
  const [clientOrganization, setClientOrganization] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  
  // Matter description
  const [matterDescription, setMatterDescription] = useState('');
  
  // Professional fees
  const [professionalFees, setProfessionalFees] = useState([
    {
      category: 'Instruction Fees',
      items: [{ description: '', amount: 0 }]
    }
  ]);
  
  // Outlays
  const [outlays, setOutlays] = useState([
    {
      category: 'Statutory Fees',
      items: [{ description: '', amount: 0 }]
    }
  ]);
  
  // VAT
  const [vatRate, setVatRate] = useState(16);
  const [applyVat, setApplyVat] = useState(true);

  // Calculated values
  const [subtotalProfessionalFees, setSubtotalProfessionalFees] = useState(0);
  const [subtotalOutlays, setSubtotalOutlays] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  // Handle QR code upload
  const handleQRUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setVerificationQR(reader.result);
        setQrPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle Stamp upload
  const handleStampUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStampImage(reader.result);
        setStampPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle Signature upload
  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureImage(reader.result);
        setSignaturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Calculate totals whenever fees change
  useEffect(() => {
    const profFeesTotal = professionalFees.reduce((total, category) => {
      return total + category.items.reduce((catTotal, item) => catTotal + (parseFloat(item.amount) || 0), 0);
    }, 0);
    
    const outlaysTotal = outlays.reduce((total, category) => {
      return total + category.items.reduce((catTotal, item) => catTotal + (parseFloat(item.amount) || 0), 0);
    }, 0);
    
    const vat = applyVat ? calculateVAT(profFeesTotal, vatRate) : 0;
    const total = calculateGrandTotal(profFeesTotal, outlaysTotal, vat);
    
    setSubtotalProfessionalFees(profFeesTotal);
    setSubtotalOutlays(outlaysTotal);
    setVatAmount(vat);
    setGrandTotal(total);
  }, [professionalFees, outlays, vatRate, applyVat]);

  // Professional fees handlers
  const addProfessionalFeeCategory = () => {
    setProfessionalFees([
      ...professionalFees,
      { category: '', items: [{ description: '', amount: 0 }] }
    ]);
  };

  const updateProfessionalFeeCategory = (index, category) => {
    const updated = [...professionalFees];
    updated[index].category = category;
    setProfessionalFees(updated);
  };

  const addProfessionalFeeItem = (categoryIndex) => {
    const updated = [...professionalFees];
    updated[categoryIndex].items.push({ description: '', amount: 0 });
    setProfessionalFees(updated);
  };

  const updateProfessionalFeeItem = (categoryIndex, itemIndex, field, value) => {
    const updated = [...professionalFees];
    updated[categoryIndex].items[itemIndex][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setProfessionalFees(updated);
  };

  const removeProfessionalFeeItem = (categoryIndex, itemIndex) => {
    const updated = [...professionalFees];
    updated[categoryIndex].items.splice(itemIndex, 1);
    if (updated[categoryIndex].items.length === 0) {
      updated.splice(categoryIndex, 1);
    }
    setProfessionalFees(updated);
  };

  // Outlays handlers
  const addOutlayCategory = () => {
    setOutlays([
      ...outlays,
      { category: '', items: [{ description: '', amount: 0 }] }
    ]);
  };

  const updateOutlayCategory = (index, category) => {
    const updated = [...outlays];
    updated[index].category = category;
    setOutlays(updated);
  };

  const addOutlayItem = (categoryIndex) => {
    const updated = [...outlays];
    updated[categoryIndex].items.push({ description: '', amount: 0 });
    setOutlays(updated);
  };

  const updateOutlayItem = (categoryIndex, itemIndex, field, value) => {
    const updated = [...outlays];
    updated[categoryIndex].items[itemIndex][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setOutlays(updated);
  };

  const removeOutlayItem = (categoryIndex, itemIndex) => {
    const updated = [...outlays];
    updated[categoryIndex].items.splice(itemIndex, 1);
    if (updated[categoryIndex].items.length === 0) {
      updated.splice(categoryIndex, 1);
    }
    setOutlays(updated);
  };

  // Form submission
  const handlePreview = () => {
    const invoiceData = {
      invoiceType,
      matterCode,
      invoiceNumber: previewInvoiceNumber(matterCode, 'INV'),
      date: invoiceDate,
      deliveryMethod,
      verificationQR,
      stampImage,
      signatureImage,
      client: {
        name: clientName,
        organization: clientOrganization,
        address: clientAddress.split('\n').filter(line => line.trim()),
        phone: clientPhone,
        email: clientEmail
      },
      matterDescription,
      professionalFees,
      outlays,
      subtotalProfessionalFees,
      subtotalOutlays,
      vatRate,
      vatAmount,
      grandTotal
    };
    
    onPreview(invoiceData);
  };

  // Common fee categories
  const professionalFeeCategories = [
    'Instruction Fees',
    'Consultation Fees',
    'Retainer Fees',
    'Legal Research',
    'Court Attendance',
    'Document Drafting',
    'Negotiation Fees'
  ];

  const outlayCategories = [
    'Statutory Fees',
    'Administrative Costs',
    'Court Filing Fees',
    'Travel and Subsistence',
    'Printing and Binding',
    'Service Fees',
    'Search Fees'
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Invoice Type Selection */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Invoice Type
        </h2>
        <div className="grid grid-cols-3 gap-4">
          {['Final Invoice', 'Draft Invoice', 'Outlay Invoice'].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setInvoiceType(type)}
              className={`p-4 rounded-lg border-2 transition-all ${
                invoiceType === type 
                  ? 'border-red-800 bg-red-50 text-red-800' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="font-semibold">{type}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Basic Information
        </h2>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">Matter/Case Code *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., L/031/22"
              value={matterCode}
              onChange={(e) => setMatterCode(e.target.value)}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Preview: {previewInvoiceNumber(matterCode, 'INV')}
            </p>
          </div>
          
          <div>
            <label className="form-label">Invoice Date *</label>
            <input
              type="date"
              className="form-input"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              required
            />
          </div>
          
          <div>
            <label className="form-label">Delivery Method</label>
            <select
              className="form-input"
              value={deliveryMethod}
              onChange={(e) => setDeliveryMethod(e.target.value)}
            >
              <option value="Courier">Delivery by Courier</option>
              <option value="Email">Email Only</option>
              <option value="Both">Courier & Email</option>
              <option value="Collection">Collection</option>
            </select>
          </div>
        </div>
      </div>

      {/* Verification QR Code Upload */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          Verification QR Code
        </h2>
        
        <div className="flex items-start gap-6">
          <div className="flex-1">
            <label className="form-label">Upload QR Code (from verify.remingtonllp.com)</label>
            <div className="mt-2">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> QR code image
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG (1.89cm × 1.89cm recommended)</p>
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleQRUpload}
                />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Generate a verification QR at <a href="https://verify.remingtonllp.com" target="_blank" rel="noopener noreferrer" className="text-red-800 hover:underline">verify.remingtonllp.com</a>
            </p>
          </div>
          
          {qrPreview && (
            <div className="flex flex-col items-center">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
              <div className="border border-gray-200 rounded-lg p-2 bg-white">
                <img 
                  src={qrPreview} 
                  alt="QR Code Preview" 
                  className="w-20 h-20 object-contain"
                  style={{ width: '71px', height: '71px' }} // 1.89cm ≈ 71px at 96dpi
                />
              </div>
              <button
                type="button"
                onClick={() => { setVerificationQR(null); setQrPreview(null); }}
                className="mt-2 text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Firm Stamp Upload */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Firm Stamp (Upload Each Time for Security)
        </h2>
        
        <div className="flex items-start gap-6">
          <div className="flex-1">
            <label className="form-label">Upload Firm Stamp Image</label>
            <div className="mt-2">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> firm stamp image
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG (1.73cm × 3.78cm recommended)</p>
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleStampUpload}
                />
              </label>
            </div>
          </div>
          
          {stampPreview && (
            <div className="flex flex-col items-center">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
              <div className="border border-gray-200 rounded-lg p-2 bg-white">
                <img 
                  src={stampPreview} 
                  alt="Stamp Preview" 
                  className="object-contain"
                  style={{ width: '65px', height: '143px' }} // 1.73cm × 3.78cm
                />
              </div>
              <button
                type="button"
                onClick={() => { setStampImage(null); setStampPreview(null); }}
                className="mt-2 text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Signature Upload */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          Partner Signature (Upload Each Time for Security)
        </h2>
        
        <div className="flex items-start gap-6">
          <div className="flex-1">
            <label className="form-label">Upload Signature Image</label>
            <div className="mt-2">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> signature image
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG (0.82cm × 2.9cm recommended)</p>
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleSignatureUpload}
                />
              </label>
            </div>
          </div>
          
          {signaturePreview && (
            <div className="flex flex-col items-center">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
              <div className="border border-gray-200 rounded-lg p-2 bg-white">
                <img 
                  src={signaturePreview} 
                  alt="Signature Preview" 
                  className="object-contain"
                  style={{ width: '31px', height: '110px' }} // 0.82cm × 2.9cm
                />
              </div>
              <button
                type="button"
                onClick={() => { setSignatureImage(null); setSignaturePreview(null); }}
                className="mt-2 text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Client Information */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Client Information (Billed To)
        </h2>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">Client/Contact Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Lands Office"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              required
            />
          </div>
          
          <div>
            <label className="form-label">Organization</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Trustees of the Catholic Archdiocese of Mombasa"
              value={clientOrganization}
              onChange={(e) => setClientOrganization(e.target.value)}
            />
          </div>
          
          <div className="col-span-2">
            <label className="form-label">Address (one line per field) *</label>
            <textarea
              className="form-input"
              rows="4"
              placeholder="Holy Ghost Cathedral&#10;Nkrumah Road&#10;PO Box 84425 – 80100&#10;Mombasa, Kenya"
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
              required
            />
          </div>
          
          <div>
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-input"
              placeholder="+254 (20) 221 1842"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
            />
          </div>
          
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              placeholder="client@example.org"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Matter Description */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Matter Description
        </h2>
        
        <div>
          <label className="form-label">Full Matter Description *</label>
          <textarea
            className="form-input"
            rows="3"
            placeholder="e.g., Kwale ELC Case 70 of 2021 (Formerly Mombasa ELC Case No. 205 of 2019) – Registered Trustees, Catholic Archdiocese of Mombasa vs. Joseph P Kariithi & Others"
            value={matterDescription}
            onChange={(e) => setMatterDescription(e.target.value)}
            required
          />
        </div>
      </div>

      {/* Professional Fees Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          I. Professional Fees
        </h2>

        {professionalFees.map((category, catIndex) => (
          <div key={catIndex} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4 mb-4">
              <select
                className="form-input flex-1"
                value={category.category}
                onChange={(e) => updateProfessionalFeeCategory(catIndex, e.target.value)}
              >
                <option value="">Select Category</option>
                {professionalFeeCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input
                type="text"
                className="form-input flex-1"
                placeholder="Or enter custom category"
                value={!professionalFeeCategories.includes(category.category) ? category.category : ''}
                onChange={(e) => updateProfessionalFeeCategory(catIndex, e.target.value)}
              />
            </div>

            {category.items.map((item, itemIndex) => (
              <div key={itemIndex} className="flex items-start gap-4 mb-3">
                <span className="text-gray-500 mt-3">{itemIndex + 1}.</span>
                <textarea
                  className="form-input flex-1"
                  rows="2"
                  placeholder="Description of fee item"
                  value={item.description}
                  onChange={(e) => updateProfessionalFeeItem(catIndex, itemIndex, 'description', e.target.value)}
                />
                <div className="w-40">
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">KES</span>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0.00"
                      step="0.01"
                      value={item.amount || ''}
                      onChange={(e) => updateProfessionalFeeItem(catIndex, itemIndex, 'amount', e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeProfessionalFeeItem(catIndex, itemIndex)}
                  className="text-red-600 hover:text-red-800 mt-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => addProfessionalFeeItem(catIndex)}
              className="text-red-800 hover:text-red-900 font-medium text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addProfessionalFeeCategory}
          className="btn-secondary flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Fee Category
        </button>

        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
          <span className="font-semibold text-gray-700">Subtotal Professional Fees:</span>
          <span className="font-bold text-lg">KES {formatCurrency(subtotalProfessionalFees)}</span>
        </div>
      </div>

      {/* Outlays Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          II. Outlays
        </h2>

        {outlays.map((category, catIndex) => (
          <div key={catIndex} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4 mb-4">
              <select
                className="form-input flex-1"
                value={category.category}
                onChange={(e) => updateOutlayCategory(catIndex, e.target.value)}
              >
                <option value="">Select Category</option>
                {outlayCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input
                type="text"
                className="form-input flex-1"
                placeholder="Or enter custom category"
                value={!outlayCategories.includes(category.category) ? category.category : ''}
                onChange={(e) => updateOutlayCategory(catIndex, e.target.value)}
              />
            </div>

            {category.items.map((item, itemIndex) => (
              <div key={itemIndex} className="flex items-start gap-4 mb-3">
                <span className="text-gray-500 mt-3">{itemIndex + 1}.</span>
                <textarea
                  className="form-input flex-1"
                  rows="2"
                  placeholder="Description of outlay item"
                  value={item.description}
                  onChange={(e) => updateOutlayItem(catIndex, itemIndex, 'description', e.target.value)}
                />
                <div className="w-40">
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">KES</span>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0.00"
                      step="0.01"
                      value={item.amount || ''}
                      onChange={(e) => updateOutlayItem(catIndex, itemIndex, 'amount', e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeOutlayItem(catIndex, itemIndex)}
                  className="text-red-600 hover:text-red-800 mt-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => addOutlayItem(catIndex)}
              className="text-red-800 hover:text-red-900 font-medium text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addOutlayCategory}
          className="btn-secondary flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Outlay Category
        </button>

        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
          <span className="font-semibold text-gray-700">Subtotal Outlays:</span>
          <span className="font-bold text-lg">KES {formatCurrency(subtotalOutlays)}</span>
        </div>
      </div>

      {/* VAT Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          III. Taxes
        </h2>

        <div className="flex items-center gap-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={applyVat}
              onChange={(e) => setApplyVat(e.target.checked)}
              className="w-5 h-5 text-red-800 rounded focus:ring-red-800"
            />
            <span className="ml-2 text-gray-700">Apply VAT</span>
          </label>
          
          {applyVat && (
            <div className="flex items-center">
              <label className="text-gray-700 mr-2">VAT Rate:</label>
              <input
                type="number"
                className="form-input w-20"
                value={vatRate}
                onChange={(e) => setVatRate(parseFloat(e.target.value) || 0)}
              />
              <span className="ml-2 text-gray-500">%</span>
            </div>
          )}
        </div>

        {applyVat && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
            <span className="text-gray-700">VAT at {vatRate}% of Professional Fees:</span>
            <span className="font-bold text-lg">KES {formatCurrency(vatAmount)}</span>
          </div>
        )}
      </div>

      {/* Grand Total & Actions */}
      <div className="bg-red-900 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-center mb-6">
          <span className="text-xl font-semibold">Grand Total (Amount Due):</span>
          <span className="text-3xl font-bold">KES {formatCurrency(grandTotal)}</span>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={handlePreview}
            className="flex-1 bg-white text-red-900 font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition-all flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview Invoice
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;
